import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, AlertCircle, CheckCircle } from 'lucide-react'
import AudioUploader from '../components/audio/AudioUploader'
import Button from '../components/ui/Button'

const UploadPage = () => {
  const [uploadResult, setUploadResult] = useState(null)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const handleUploadSuccess = (result) => {
    setUploadResult(result)
    setError(null)
  }

  const handleUploadError = (errorMessage) => {
    setError(errorMessage)
    setUploadResult(null)
  }

  const handleViewResult = () => {
    if (uploadResult) {
      navigate(`/transcription/${uploadResult.id}`)
    }
  }

  const handleUploadAnother = () => {
    setUploadResult(null)
    setError(null)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 页面头部 */}
        <div className="text-center mb-12">
          <Upload className="mx-auto h-16 w-16 text-primary-600 mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            上传音频文件
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            上传您的音频文件，我们将自动为您转录并识别不同的说话者
          </p>
        </div>

        {/* 上传成功状态 */}
        {uploadResult && (
          <div className="mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-green-800">
                    文件上传成功！
                  </h3>
                  <p className="text-green-700">
                    {uploadResult.original_filename} 已开始处理
                  </p>
                </div>
              </div>
              
              <div className="bg-green-100 rounded-md p-4 mb-4">
                <p className="text-sm text-green-800">
                  🎉 转录任务已创建，正在后台处理中...
                </p>
                <p className="text-sm text-green-700 mt-1">
                  处理时间通常为音频时长的 1-3 倍，请耐心等待。您可以查看处理进度或上传其他文件。
                </p>
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleViewResult}>
                  查看转录进度
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleUploadAnother}
                >
                  上传其他文件
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 错误状态 */}
        {error && (
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <AlertCircle className="h-8 w-8 text-red-400 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">
                    上传失败
                  </h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 音频上传器 */}
        {!uploadResult && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <AudioUploader
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
            />
          </div>
        )}

        {/* 功能说明 */}
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-primary-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <Upload className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              简单上传
            </h3>
            <p className="text-gray-600">
              支持拖拽上传，兼容 MP3、WAV、M4A 等主流音频格式
            </p>
          </div>

          <div className="text-center">
            <div className="bg-primary-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <span className="text-primary-600 font-bold text-lg">AI</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              AI 转录
            </h3>
            <p className="text-gray-600">
              使用 Deepgram AI 技术，提供业界领先的转录准确率
            </p>
          </div>

          <div className="text-center">
            <div className="bg-primary-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <span className="text-primary-600 font-bold">👥</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              说话者识别
            </h3>
            <p className="text-gray-600">
              自动识别不同说话者，生成结构化的会议记录
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadPage